import React, { Component, ErrorInfo, ReactNode } from 'react';
import { DashboardErrorFallback } from './DashboardErrorFallback';

interface DashboardErrorBoundaryProps {
  children: ReactNode;
}

interface DashboardErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary specifically for the Dashboard component
 * Captures any errors that occur during dashboard rendering or data fetching
 */
export class DashboardErrorBoundary extends Component<DashboardErrorBoundaryProps, DashboardErrorBoundaryState> {
  constructor(props: DashboardErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): DashboardErrorBoundaryState {
    console.error('Dashboard error caught by boundary:', error);
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to the console
    console.error('Dashboard rendering error:', error);
    console.error('Component stack:', errorInfo.componentStack);
  }
  
  resetErrorBoundary = () => {
    this.setState({
      hasError: false,
      error: null
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <DashboardErrorFallback 
          error={this.state.error} 
          resetErrorBoundary={this.resetErrorBoundary} 
        />
      );
    }

    // Otherwise, render the children
    return this.props.children;
  }
} 